'use client';

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { ThemeDefinition, getThemeDefinition, isValidTheme, getAvailableThemes } from './theme-config';

interface ThemeContextType {
  theme: string;
  setTheme: (themeId: string, options?: { persist?: boolean }) => void;
  toggleTheme: () => void;
  themeDefinition: ThemeDefinition | null;
  availableThemes: string[];
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const THEME_STORAGE_KEY = 'app-theme';
const DEFAULT_THEME = 'dark';

/**
 * Compute CSS variables from theme definition
 * 
 * This is where the magic happens - themes are computed from properties,
 * not hardcoded. This allows infinite scalability.
 */
function computeThemeVariables(theme: ThemeDefinition): Record<string, string> {
  const { category, primaryHue, primarySaturation, primaryLightness, overrides, cssVarExtras } = theme;
  const isDark = category === 'dark';
  
  // Compute base colors from category and properties
  // Dark theme: production computed background rgb(10, 10, 10) / HSL 0 0% 3.9%
  const background = overrides?.background || (
    isDark
      ? `rgb(10, 10, 10)`
      : `hsl(0 0% 100%)`
  );

  // Page background gradient (for full-page backgrounds; production MDR uses bg-gradient-to-br from-black to-slate-900)
  const pageBackgroundGradient = overrides?.pageBackgroundGradient || overrides?.backgroundGradient || (
    isDark
      ? `linear-gradient(to bottom right, black, rgb(15, 23, 42))`  // from-black to-slate-900
      : `linear-gradient(to bottom right, hsl(0 0% 98%), hsl(0 0% 100%))`
  );

  // Cards/dialogs: production login dialog 0.076,0.097,0.145 → rgb(19, 25, 37)
  // Light: slate-50 equivalent rgb(248, 250, 252) — linear sRGB ~0.974 / 0.980 / 0.987
  const card = overrides?.card || (
    isDark
      ? `rgb(19, 25, 37)`
      : `rgb(248, 250, 252)`
  );

  const popover =
    overrides?.popover || (isDark ? `rgb(19, 25, 37)` : `hsl(0 0% 100%)`);

  // Production: primary text (MDR table, app-wide) 0.891, 0.909, 0.938 → rgb(227, 232, 239)
  const foreground = overrides?.foreground || (
    isDark
      ? `rgb(227, 232, 239)`
      : `hsl(0 0% 3.9%)`
  );

  // Production: subtext "Enter your email..." 0.591,0.637,0.714 → rgb(151, 162, 182)
  const foregroundMuted =
    overrides?.foregroundMuted ||
    (isDark ? `rgb(151, 162, 182)` : `hsl(0 0% 45.1%)`);
  const foregroundSubtle =
    overrides?.foregroundSubtle ||
    (isDark ? `rgb(151, 162, 182)` : `hsl(0 0% 60%)`);

  // Single source of truth for borders: applied globally via * { border-color: var(--theme-border) }.
  // Dark: crisp visible border (production ~#3F4C5E). Light: standard. Change here to affect all themes.
  const border = overrides?.border || (
    isDark
      ? `rgb(33, 41, 55)`   /* 0.129 0.160 0.215 from color picker */
      : `hsl(0 0% 89.8%)`
  );
  const borderSubtle = overrides?.borderSubtle || (
    isDark
      ? `rgb(71, 85, 105)`   /* slate-600, slightly lighter than border */
      : `hsl(0 0% 85%)`
  );
  const borderStrong = overrides?.borderStrong || (
    isDark
      ? `hsl(0 0% 83.1%)`
      : `hsl(0 0% 3.9%)`
  );

  // Production: inside text box 0.146,0.179,0.245 → rgb(37, 46, 62)
  const input =
    overrides?.input ||
    (isDark ? `rgb(37, 46, 62)` : `hsl(0 0% 89.8%)`);

  /** Tailwind `muted` surface (cards/chips); CYDEcore maps this to `--surface`. */
  const mutedSurface =
    overrides?.muted || (isDark ? border : `hsl(0 0% 96.1%)`);
  
  // Accent colors computed from primary properties
  const accent = overrides?.accent || `hsl(${primaryHue} ${primarySaturation}% ${primaryLightness}%)`;
  const accentHover =
    overrides?.accentHover ||
    `hsl(${primaryHue} ${primarySaturation}% ${Math.max(0, primaryLightness - 5)}%)`;
  const accentForeground = `hsl(0 0% 100%)`;
  
  const accentMuted =
    overrides?.accentMuted ||
    (isDark
      ? `hsl(${primaryHue} 33% 18%)`
      : `hsl(${primaryHue} ${primarySaturation}% 95%)`);
  
  const accentMutedForeground =
    overrides?.accentMutedForeground ||
    (isDark
      ? `hsl(0 0% 98%)`
      : `hsl(${primaryHue} ${primarySaturation}% 30%)`);
  
  // Status colors (theme-aware for better contrast)
  const success =
    overrides?.success ||
    (isDark ? `hsl(142 76% 36%)` : `hsl(142 71% 45%)`);
  // Warning: red-orange for dark theme (more red than amber), darker orange for light theme
  const warning =
    overrides?.warning ||
    (isDark ? `hsl(25 92% 55%)` : `hsl(39 92% 38%)`);
  // Warning background: semi-transparent version for alert backgrounds
  const warningBackground =
    overrides?.warningBackground ||
    (isDark ? `hsla(25, 92%, 55%, 0.15)` : `hsla(39, 92%, 38%, 0.15)`);
  // Error: brighter red for dark theme (better contrast), standard red for light theme
  const error =
    overrides?.error ||
    (isDark ? `hsl(0 72% 55%)` : `hsl(0 84% 60%)`);
  const info = accent;
  
  // Interactive states
  const hover = isDark
    ? `hsl(${primaryHue} 33% 18%)`
    : `hsl(0 0% 96%)`;
  
  const active = isDark
    ? `hsl(${primaryHue} 33% 20%)`
    : `hsl(0 0% 92%)`;
  
  const selected = accent;
  const selectedForeground = accentForeground;
  
  // Input focus uses accent color
  const inputFocus = accent;

  // Production MDR: table row background 0.037,0.043,0.065 → rgb(9, 11, 17)
  const tableRowBg = isDark ? `rgb(9, 11, 17)` : `hsl(0 0% 98%)`;
  // Production MDR: empty table area ("0 cases") 0.033,0.039,0.057 → rgb(8, 10, 15)
  const tableEmptyBg = isDark ? `rgb(8, 10, 15)` : `hsl(0 0% 98%)`;
  // Production MDR: table empty/loading background gradient 0.028,0.031,0.042 → rgb(7,8,11) to 0.041,0.051,0.073 → rgb(10,13,19)
  const tableEmptyGradient = isDark
    ? `linear-gradient(to bottom, rgb(7, 8, 11), rgb(10, 13, 19))`
    : `linear-gradient(to bottom, hsl(0 0% 98%), hsl(0 0% 96%))`;
  // Production MDR: filter cards 0.157,0.187,0.249 → rgb(40, 48, 63)
  const cardElevated = isDark ? `rgb(40, 48, 63)` : `hsl(0 0% 96%)`;
  // Production MDR: header and footer (app chrome) 0.061,0.085,0.152 → rgb(16, 22, 39)
  const headerFooter =
    overrides?.headerFooter ||
    (isDark ? `rgb(16, 22, 39)` : `hsl(0 0% 98%)`);
  // Production MDR: table row divider 0.149,0.149,0.149 → rgb(38, 38, 38)
  const tableDivider = isDark ? `rgb(38, 38, 38)` : `hsl(0 0% 89.8%)`;
  // Production MDR: table header/footer 0.109,0.136,0.191 → rgb(28, 35, 49)
  const tableHeader = isDark ? `rgb(28, 35, 49)` : `hsl(0 0% 96%)`;
  // Production MDR: table header column dividers (vertical lines) 0.289, 0.332, 0.405 → rgb(74, 85, 103)
  const tableHeaderDivider = isDark ? `rgb(74, 85, 103)` : `hsl(0 0% 85%)`;
  // Production MDR: escalated row 0.138,0.074,0.035 → rgb(35, 19, 9)
  const rowEscalated = isDark ? `rgb(35, 19, 9)` : `hsl(38 60% 95%)`;
  // Production MDR: search box inside 0.139,0.164,0.211 → rgb(35, 42, 54)
  const inputSearch = isDark ? `rgb(35, 42, 54)` : `hsl(0 0% 89.8%)`;
  // Production MDR: lookback dropdown (7 days, etc.) 0.029, 0.035, 0.046 → rgb(7, 9, 12)
  const selectContentBg = isDark ? `rgb(7, 9, 12)` : `hsl(0 0% 100%)`;
  // Production MDR: filter container 0.079,0.097,0.134 → rgb(20, 25, 34); gradient more pronounced (lighter top, darker bottom)
  const filterContainer = isDark ? `rgb(20, 25, 34)` : `hsl(0 0% 98%)`;
  const filterContainerGradient = isDark
    ? `linear-gradient(to bottom, rgb(44, 52, 65), rgb(16, 20, 28))`
    : `linear-gradient(to bottom, hsl(0 0% 98%), hsl(0 0% 96%))`;
  // Production MDR: container for metrics breakdown, search, Escalated toggle, Clear Filters, Refresh, Export, Columns 0.032,0.035,0.050 → rgb(8, 9, 13)
  const mdrControlsContainer = isDark ? `rgb(8, 9, 13)` : `hsl(0 0% 98%)`;
  // Production MDR: container surrounding filter cards uses bg-background/50 + border-surface-dark/50 + backdrop-blur
  const mdrContainerBg = isDark ? `rgba(9, 11, 17, 0.5)` : `rgba(255, 255, 255, 0.5)`;
  const mdrContainerBorder = isDark ? `rgba(71, 85, 105, 0.65)` : `rgba(148, 163, 184, 0.6)`;
  // Production MDR: inner filter cards (Case Status / Case Severity) bg-slate-800/50 border-slate-700/50
  const filterCardBg = isDark ? `rgba(30, 41, 59, 0.5)` : `rgba(248, 250, 252, 0.8)`;
  const filterCardBorder = isDark ? `rgba(51, 65, 85, 0.5)` : `rgba(203, 213, 225, 0.5)`;

  // Chart-specific colors (theme-aware)
  const chartBg = card;
  const chartGrid = isDark ? `hsl(${primaryHue} 33% 20%)` : `hsl(0 0% 85%)`;
  const chartAxis = isDark ? `hsl(0 0% 64%)` : `hsl(0 0% 60%)`;
  const chartText = isDark ? `hsl(0 0% 64%)` : `hsl(0 0% 45%)`;
  const chartTextMuted = isDark ? `hsl(0 0% 45%)` : `hsl(0 0% 70%)`;
  
  // Extract HSL values for Tailwind compatibility (format: "h s% l%")
  // Tailwind expects: "h s% l%" (without hsl() wrapper)
  const extractHSL = (colorString: string): string => {
    // Handle rgba values - convert to HSL equivalent (without opacity)
    const rgbaMatch = colorString.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)/);
    if (rgbaMatch) {
      const r = parseInt(rgbaMatch[1]) / 255;
      const g = parseInt(rgbaMatch[2]) / 255;
      const b = parseInt(rgbaMatch[3]) / 255;
      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      const l = (max + min) / 2;
      let h = 0, s = 0;
      if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        else if (max === g) h = ((b - r) / d + 2) / 6;
        else h = ((r - g) / d + 4) / 6;
      }
      return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
    }
    // Match hsl(h s% l%) format
    const hslMatch = colorString.match(/hsl\((\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)%\s+(\d+(?:\.\d+)?)%\)/);
    if (hslMatch) {
      return `${hslMatch[1]} ${hslMatch[2]}% ${hslMatch[3]}%`;
    }
    // If already in the right format, return as-is
    if (!colorString.includes('hsl(') && !colorString.includes('rgba')) {
      return colorString;
    }
    // Fallback: try to extract from any format
    return colorString.replace(/hsl\(|rgba?\(|\)/g, '').trim();
  };

  const computed = {
    '--theme-background': background,
    '--theme-background-gradient': overrides?.backgroundGradient || background,
    '--theme-page-background': pageBackgroundGradient,
    '--theme-card': card,
    '--theme-card-hover':
      overrides?.cardHover ||
      (isDark ? `rgb(28, 34, 48)` : `hsl(0 0% 92%)`), // Slightly lighter than card on hover
    '--theme-card-elevated': cardElevated,
    '--theme-dashboard-widget-bg': isDark ? `rgb(13, 15, 21)` : `rgb(255, 255, 255)`,  // Dashboard widgets: production dark 0.049 0.058 0.084
    '--theme-table-row': tableRowBg,
    '--theme-table-empty': tableEmptyBg,
    '--theme-table-empty-gradient': tableEmptyGradient,
    '--theme-header-footer': headerFooter,
    '--theme-table-divider': tableDivider,
    '--theme-table-header': tableHeader,
    '--theme-table-header-divider': tableHeaderDivider,
    '--theme-row-escalated': rowEscalated,
    '--theme-input-search': inputSearch,
    '--theme-select-content-bg': selectContentBg,
    '--theme-filter-container': filterContainer,
    '--theme-filter-container-gradient': filterContainerGradient,
    '--theme-mdr-controls-container': mdrControlsContainer,
    '--theme-mdr-container-bg': mdrContainerBg,
    '--theme-mdr-container-border': mdrContainerBorder,
    '--theme-filter-card-bg': filterCardBg,
    '--theme-filter-card-border': filterCardBorder,
    '--theme-popover': popover,
    '--theme-foreground': foreground,
    '--theme-foreground-muted': foregroundMuted,
    '--theme-foreground-subtle': foregroundSubtle,
    '--theme-border': border,
    '--theme-border-subtle': borderSubtle,
    '--theme-border-strong': borderStrong,
    // Special border for dialogs/cards (semi-transparent slate-700)
    '--theme-border-dialog': isDark ? `rgba(51, 65, 85, 0.5)` : `hsl(0 0% 89.8%)`,
    // Muted background for info boxes (semi-transparent slate-700, lighter than dialog border)
    '--theme-info-box-bg': isDark ? `rgba(51, 65, 85, 0.3)` : `hsla(0, 0%, 0%, 0.05)`,
    // Info pill background (semi-transparent blue)
    '--theme-info-pill-bg': isDark ? `rgba(59, 130, 246, 0.2)` : `rgba(59, 130, 246, 0.1)`,
    '--theme-input': input,
    '--theme-input-focus': inputFocus,
    '--theme-accent': accent,
    '--theme-accent-hover': accentHover,
    '--theme-accent-foreground': accentForeground,
    '--theme-accent-muted': accentMuted,
    '--theme-accent-muted-foreground': accentMutedForeground,
    '--theme-success': success,
    '--theme-warning': warning,
    '--theme-warning-background': warningBackground,
    '--theme-error': error,
    '--theme-info': info,
    '--theme-hover': hover,
    '--theme-active': active,
    '--theme-selected': selected,
    '--theme-selected-foreground': selectedForeground,
    // Chart variables
    '--chart-bg': chartBg,
    '--chart-grid': chartGrid,
    '--chart-axis': chartAxis,
    '--chart-text': chartText,
    '--chart-text-muted': chartTextMuted,
    '--chart-high': '#f97316', /* Orange - matches High severity badge */
    '--chart-medium': '#eab308', /* Yellow - matches Medium severity badge, more distinguishable from High for color blindness */
    '--chart-low': '#2563eb', /* Blue - matches Low severity badge */
    '--chart-info': '#7c3aed', /* Purple - matches Info severity badge */
    '--chart-critical': '#dc2626', /* Red - matches Critical severity badge */
    // Legacy Tailwind variables (HSL "h s% l%" for Tailwind hsl() usage)
    '--background': extractHSL(background),
    '--foreground': extractHSL(foreground),
    '--card': extractHSL(card),
    '--card-foreground': extractHSL(foreground),
    '--popover': extractHSL(popover),
    '--popover-foreground': extractHSL(foreground),
    '--primary': extractHSL(accent),
    '--primary-foreground': extractHSL(accentForeground),
    '--secondary': isDark ? extractHSL(input) : '0 0% 96.1%',
    '--secondary-foreground': extractHSL(foreground),
    '--muted': isDark ? extractHSL(mutedSurface) : '0 0% 96.1%',
    '--muted-foreground': extractHSL(foregroundMuted),
    '--accent': isDark ? extractHSL(input) : '0 0% 96.1%',
    '--accent-foreground': extractHSL(foreground),
    '--destructive': isDark ? '0 62.8% 30.6%' : extractHSL(error),
    '--destructive-foreground': extractHSL(accentForeground),
    '--border': extractHSL(border),
    '--input': extractHSL(input),
    '--ring': isDark ? '0 0% 83.1%' : extractHSL(borderStrong),
  };

  return { ...computed, ...(cssVarExtras || {}) };
}

/**
 * Theme Provider Component
 * 
 * Manages global theme state and applies computed CSS variables to document root.
 * Supports infinite themes through property-based computation.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<string>(DEFAULT_THEME);
  const [mounted, setMounted] = useState(false);

  // Load theme from localStorage on mount
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);
    // Never restore the ephemeral tenant-picker theme (AuthEnforcer may have persisted it by mistake)
    if (savedTheme && isValidTheme(savedTheme) && savedTheme !== 'tenant-selector') {
      setThemeState(savedTheme);
      applyTheme(savedTheme);
    } else {
      if (savedTheme === 'tenant-selector') {
        localStorage.removeItem(THEME_STORAGE_KEY);
      }
      applyTheme(DEFAULT_THEME);
    }
    setMounted(true);
  }, []);

  // Apply theme to document
  const applyTheme = useCallback((themeId: string) => {
    if (typeof window === 'undefined') return;
    
    const theme = getThemeDefinition(themeId);
    if (!theme) {
      console.warn(`Theme "${themeId}" not found`);
      return;
    }
    
    const root = document.documentElement;
    const variables = computeThemeVariables(theme);
    
    // Remove all existing theme classes
    const allThemeIds = getAvailableThemes();
    allThemeIds.forEach(id => root.classList.remove(id));

    Array.from(root.classList).forEach((className) => {
      if (className.startsWith("theme-")) root.classList.remove(className);
    });
    
    // Add current theme class
    root.classList.add(themeId);
    
    // Add category and color scheme classes for additional styling
    root.classList.add(`theme-${theme.category}`, `theme-${theme.colorScheme}`);
    
    // Apply computed CSS variables
    Object.entries(variables).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });
  }, []);

  // Update theme
  const setTheme = useCallback((newThemeId: string, options?: { persist?: boolean }) => {
    if (!isValidTheme(newThemeId)) {
      console.warn(`Invalid theme: ${newThemeId}`);
      return;
    }
    
    setThemeState(newThemeId);
    applyTheme(newThemeId);
    
    // Persist to localStorage (never persist ephemeral picker theme)
    const shouldPersist = options?.persist !== false && newThemeId !== 'tenant-selector';
    if (typeof window !== 'undefined' && shouldPersist) {
      localStorage.setItem(THEME_STORAGE_KEY, newThemeId);
      // Dispatch custom event for components that listen to theme changes
      window.dispatchEvent(new CustomEvent('themechange', { detail: { theme: newThemeId } }));
    } else if (typeof window !== 'undefined' && newThemeId === 'tenant-selector') {
      window.dispatchEvent(new CustomEvent('themechange', { detail: { theme: newThemeId } }));
    }
  }, [applyTheme]);

  // Toggle between dark and light (for backward compatibility)
  const toggleTheme = useCallback(() => {
    const currentTheme = getThemeDefinition(theme);
    if (!currentTheme) return;
    
    // Find opposite category theme
    const oppositeCategory = currentTheme.category === 'dark' ? 'light' : 'dark';
    const oppositeTheme = getAvailableThemes().find(id => {
      const def = getThemeDefinition(id);
      return def?.category === oppositeCategory;
    });
    
    if (oppositeTheme) {
      setTheme(oppositeTheme);
    }
  }, [theme, setTheme]);

  // Listen for theme changes from other tabs/windows
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === THEME_STORAGE_KEY && e.newValue && isValidTheme(e.newValue)) {
        setThemeState(e.newValue);
        applyTheme(e.newValue);
      }
    };
    
    const handleThemeChange = (e: CustomEvent) => {
      const newTheme = e.detail?.theme;
      if (newTheme && isValidTheme(newTheme)) {
        setThemeState(newTheme);
        applyTheme(newTheme);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('themechange', handleThemeChange as EventListener);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('themechange', handleThemeChange as EventListener);
    };
  }, [applyTheme]);

  // Apply theme when it changes
  useEffect(() => {
    if (mounted) {
      applyTheme(theme);
    }
  }, [theme, applyTheme, mounted]);

  const value: ThemeContextType = {
    theme,
    setTheme,
    toggleTheme,
    themeDefinition: getThemeDefinition(theme),
    availableThemes: getAvailableThemes(),
  };

  // Provide default context during SSR to prevent errors
  // The actual theme will be applied once mounted on client
  const defaultValue: ThemeContextType = {
    theme: DEFAULT_THEME,
    setTheme: () => {},
    toggleTheme: () => {},
    themeDefinition: getThemeDefinition(DEFAULT_THEME),
    availableThemes: getAvailableThemes(),
  };

  // Prevent flash of unstyled content
  if (!mounted) {
    return <ThemeContext.Provider value={defaultValue}>{children}</ThemeContext.Provider>;
  }

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/**
 * Hook to use theme context
 */
export function useTheme(): ThemeContextType {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
